import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UserPlus, Users, ChevronDown, ChevronRight, Edit2, X, Check, Eye, EyeOff, Trash2 } from 'lucide-react';

// Class dasar buat native <select>, disamain sama form-form lain
// (border-slate-200, rounded-xl, focus ring warna primary #1A3C34)
const selectBaseClass =
  'flex h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1A3C34] focus-visible:border-[#1A3C34]';

// Versi lebih kecil buat dropdown edit inline di dalam tabel
const selectInlineClass =
  'h-9 rounded-xl border border-slate-200 bg-white px-2.5 py-1 text-sm text-slate-700 min-w-[180px] ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1A3C34] focus-visible:border-[#1A3C34]';

export default function EmployeesManagement() {
  const [employees, setEmployees] = useState([]);
  const [hierarchy, setHierarchy] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editSupervisorId, setEditSupervisorId] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    supervisor_id: ''
  });

  useEffect(() => {
    fetchEmployees();
    fetchHierarchy();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/users/employees');
      setEmployees(response.data);
    } catch (error) {
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const fetchHierarchy = async () => {
    try {
      const response = await api.get('/users/hierarchy');
      setHierarchy(response.data);
    } catch (error) {
      console.error('Failed to load hierarchy:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/users/employees', {
        ...formData,
        supervisor_id: formData.supervisor_id || null
      });
      toast.success('Employee created successfully!');
      setShowForm(false);
      setFormData({ email: '', name: '', password: '', supervisor_id: '' });
      fetchEmployees();
      fetchHierarchy();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create employee');
    }
  };

  const startEditSupervisor = (emp) => {
    setEditingId(emp.id);
    setEditSupervisorId(emp.supervisor_id || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditSupervisorId('');
  };

  const saveSupervisor = async (employeeId) => {
    try {
      await api.put(`/users/employees/${employeeId}/supervisor`, {
        supervisor_id: editSupervisorId || null
      });
      toast.success('Supervisor updated!');
      setEditingId(null);
      setEditSupervisorId('');
      fetchEmployees();
      fetchHierarchy();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update supervisor');
    }
  };

  const handleDeleteEmployee = async (employeeId, employeeName) => {
    if (!window.confirm(`Yakin ingin menghapus akun ${employeeName}?`)) {
      return;
    }
    try {
      await api.delete(`/users/${employeeId}`);
      toast.success('Employee deleted successfully!');
      fetchEmployees();
      fetchHierarchy();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete employee');
    }
  };

  const HierarchyNode = ({ node, level = 0 }) => {
    const [expanded, setExpanded] = useState(level === 0);
    const hasSubordinates = node.subordinates && node.subordinates.length > 0;

    return (
      <div className="ml-4">
        <div className="flex items-center gap-2 py-2 hover:bg-[#1A3C34]/5 rounded-xl px-2 transition-colors">
          {hasSubordinates && (
            <button onClick={() => setExpanded(!expanded)} className="p-1">
              {expanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
            </button>
          )}
          {!hasSubordinates && <div className="w-6" />}

          <div className="flex items-center gap-2 flex-1">
            <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${
              node.role === 'admin' ? 'bg-[#1A3C34]/10 text-[#1A3C34]' : 'bg-[#F97316]/10 text-[#F97316]'
            }`}>
              <Users className="w-4 h-4" />
            </div>
            <div>
              <p className="font-semibold text-slate-900">{node.user_name}</p>
              <p className="text-xs text-muted-foreground">{node.email} &bull; {node.role}</p>
            </div>
          </div>

          {hasSubordinates && (
            <span className="text-xs text-muted-foreground">
              {node.subordinates.length} subordinate{node.subordinates.length > 1 ? 's' : ''}
            </span>
          )}
        </div>

        {expanded && hasSubordinates && (
          <div className="border-l-2 border-slate-100 ml-4">
            {node.subordinates.map((sub) => (
              <div key={sub.user_id || sub.id}>
                {React.createElement(HierarchyNode, { node: sub, level: level + 1 })}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page Header — samain sama halaman lain */}
      <div className="pb-2 border-b border-slate-200/70 flex justify-between items-center flex-wrap gap-3">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold font-heading text-slate-900 tracking-tight" data-testid="employees-mgmt-title">
            Employees Management
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Manage employees and supervisor hierarchy</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={() => setViewMode('list')}
            data-testid="list-view-btn"
            className={
              viewMode === 'list'
                ? 'bg-[#1A3C34] hover:bg-[#142E27] text-white font-medium rounded-xl shadow-md transition-all duration-200 active:scale-[0.99]'
                : 'rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-800 bg-white'
            }
          >
            List View
          </Button>
          <Button
            onClick={() => setViewMode('hierarchy')}
            data-testid="hierarchy-view-btn"
            className={
              viewMode === 'hierarchy'
                ? 'bg-[#1A3C34] hover:bg-[#142E27] text-white font-medium rounded-xl shadow-md transition-all duration-200 active:scale-[0.99]'
                : 'rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-800 bg-white'
            }
          >
            Hierarchy View
          </Button>
          <Button
            onClick={() => { setShowForm(!showForm); cancelEdit(); }}
            data-testid="add-employee-btn"
            className="bg-[#1A3C34] hover:bg-[#142E27] text-white font-medium rounded-xl shadow-md transition-all duration-200 active:scale-[0.99]"
          >
            <UserPlus className="w-5 h-5 mr-2" />
            {showForm ? 'Cancel' : 'Add Employee'}
          </Button>
        </div>
      </div>

      {showForm ? (
        /* Add Employee Form — list/hierarchy disembunyikan selama form terbuka */
        <Card className="p-6 bg-white border-slate-200/80 shadow-sm">
          <div className="pb-4 border-b border-slate-100 mb-6">
            <h2 className="text-xl font-bold font-heading text-slate-900">Add New Employee</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Isi detail akun karyawan baru</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium text-slate-700">Full Name *</Label>
                <Input
                  id="name"
                  data-testid="emp-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="John Doe"
                  className="w-full border-slate-200 rounded-xl text-sm placeholder:text-slate-300 placeholder:font-normal focus-visible:ring-[#1A3C34] focus-visible:border-[#1A3C34]"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="emp-email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="john@company.com"
                  className="w-full border-slate-200 rounded-xl text-sm placeholder:text-slate-300 placeholder:font-normal focus-visible:ring-[#1A3C34] focus-visible:border-[#1A3C34]"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">Password *</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    data-testid="emp-password-input"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    placeholder="••••••••"
                    className="w-full border-slate-200 rounded-xl text-sm pr-10 placeholder:text-slate-300 placeholder:font-normal focus-visible:ring-[#1A3C34] focus-visible:border-[#1A3C34]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    data-testid="toggle-emp-password-visibility"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    tabIndex={-1}
                    title={showPassword ? 'Sembunyikan password' : 'Tampilkan password'}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="supervisor" className="text-sm font-medium text-slate-700">Supervisor (Optional)</Label>
                <select
                  id="supervisor"
                  data-testid="emp-supervisor-select"
                  value={formData.supervisor_id}
                  onChange={(e) => setFormData({ ...formData, supervisor_id: e.target.value })}
                  className={selectBaseClass}
                >
                  <option value="">No Supervisor</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name} ({emp.email})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                type="submit"
                data-testid="create-employee-btn"
                className="bg-[#1A3C34] hover:bg-[#142E27] text-white font-medium rounded-xl shadow-md transition-all duration-200 active:scale-[0.99]"
              >
                Create Employee
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowForm(false);
                  setFormData({ name: '', email: '', password: '', supervisor_id: '' });
                  setShowPassword(false);
                }}
                className="rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-800"
              >
                Batal
              </Button>
            </div>
          </form>
        </Card>
      ) : viewMode === 'list' ? (
        <Card className="p-6 bg-white border-slate-200/80 shadow-sm">
          <div className="pb-4 border-b border-slate-100 mb-6">
            <h2 className="text-xl font-bold font-heading text-slate-900">Employees List</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Daftar seluruh karyawan dan supervisor-nya</p>
          </div>
          {employees.length === 0 ? (
            <p className="text-muted-foreground text-center py-8 text-sm">No employees yet. Add one above.</p>
          ) : (
            <div className="rounded-2xl border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="employees-table">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold text-xs uppercase tracking-wider">
                      <th className="text-left py-3.5 px-4">Name</th>
                      <th className="text-left py-3.5 px-4">Email</th>
                      <th className="text-left py-3.5 px-4">Supervisor</th>
                      <th className="text-left py-3.5 px-4">Subordinates</th>
                      <th className="text-left py-3.5 px-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {employees.map((emp) => (
                      <tr key={emp.id} className="hover:bg-[#1A3C34]/5 transition-colors" data-testid={`employee-row-${emp.id}`}>
                        <td className="py-3 px-4 font-semibold text-slate-900">{emp.name}</td>
                        <td className="py-3 px-4 text-slate-600">{emp.email}</td>
                        <td className="py-3 px-4">
                          {editingId === emp.id ? (
                            <div className="flex items-center gap-2">
                              <select
                                data-testid={`edit-supervisor-select-${emp.id}`}
                                value={editSupervisorId}
                                onChange={(e) => setEditSupervisorId(e.target.value)}
                                className={selectInlineClass}
                              >
                                <option value="">No Supervisor</option>
                                {employees.filter(e => e.id !== emp.id).map((e) => (
                                  <option key={e.id} value={e.id}>
                                    {e.name}
                                  </option>
                                ))}
                              </select>
                              <button
                                onClick={() => saveSupervisor(emp.id)}
                                data-testid={`save-supervisor-btn-${emp.id}`}
                                className="p-1.5 rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors"
                                title="Save"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                              <button
                                onClick={cancelEdit}
                                data-testid={`cancel-edit-btn-${emp.id}`}
                                className="p-1.5 rounded-lg bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                                title="Cancel"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <span className="text-slate-700">{emp.supervisor_name || '-'}</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {emp.has_subordinates ? (
                            <span className="text-[#1A3C34] font-semibold">Has subordinates</span>
                          ) : (
                            <span className="text-muted-foreground">None</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {editingId !== emp.id && (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => startEditSupervisor(emp)}
                                data-testid={`edit-supervisor-btn-${emp.id}`}
                                className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                                title="Edit Supervisor"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteEmployee(emp.id, emp.name)}
                                data-testid={`delete-employee-btn-${emp.id}`}
                                className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 hover:text-red-700 transition-colors"
                                title="Delete Employee"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Card>
      ) : (
        <Card className="p-6 bg-white border-slate-200/80 shadow-sm">
          <div className="pb-4 border-b border-slate-100 mb-6">
            <h2 className="text-xl font-bold font-heading text-slate-900">Organization Hierarchy</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Struktur supervisor dan bawahannya</p>
          </div>
          {hierarchy.length === 0 ? (
            <p className="text-muted-foreground text-center py-8 text-sm">No hierarchy data</p>
          ) : (
            <div className="space-y-2">
              {hierarchy.map((node) => (
                <div key={node.user_id}>
                  {React.createElement(HierarchyNode, { node: node })}
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}